/**
 * CoffeeMates Events Service
 */
import httpClient from '../utils/httpClient';

export interface Zone {
  id: string;
  name: string;
  metro_stations: string[];
  partner_count: number;
}

export interface EventItem {
  id: string;
  title: string;
  description: string | null;
  topic_brief: string | null;
  game_activity: string | null;
  event_type: 'professional' | 'social' | 'masterclass' | 'knowledge_session';
  source: string;
  voucher_tier: string;
  zone_id: string;
  venue_id: string | null;
  scheduled_date: string;
  scheduled_time: string | null;
  duration_minutes: number;
  signup_deadline: string | null;
  min_capacity: number;
  max_capacity: number;
  current_count: number;
  waitlist_count: number;
  spots_remaining: number;
  is_full: boolean;
  status: string;
  avg_rating: number | null;
  rating_count: number;
  masterclass_fee?: number;
  created_by?: string | null;
  requires_approval?: boolean;
  match_reason?: string;
  match_score?: number;
  user_has_voucher?: boolean;
  user_voucher_status?: string | null;
  is_trending?: boolean;
  venue?: {
    id: string;
    name: string;
    photo_url: string | null;
    address: string;
    tier: string;
    coffeemates_menu?: unknown;
    courtesy_discount?: string | null;
    slug?: string | null;
  };
  zone?: Zone;
  attendees?: { first_name: string; avatar_url: string | null; industry?: string }[];
}

// Optional AbortSignal lets callers (esp. useQuery) cancel in-flight
// requests; opts is only forwarded when a signal is present so existing
// call-site signatures stay untouched.
const eventsService = {
  async getRecommended(signal?: AbortSignal): Promise<EventItem[]> {
    const { data } = signal
      ? await httpClient.get<EventItem[]>('/events/recommended', { signal })
      : await httpClient.get<EventItem[]>('/events/recommended');
    return data;
  },

  async getZones(signal?: AbortSignal): Promise<Zone[]> {
    const { data } = signal
      ? await httpClient.get<Zone[]>('/events/zones', { signal })
      : await httpClient.get<Zone[]>('/events/zones');
    return data;
  },

  async getWeekly(filters?: { zone_id?: string; event_type?: string; voucher_tier?: string; day?: string }, signal?: AbortSignal): Promise<EventItem[]> {
    const params = new URLSearchParams();
    if (filters?.zone_id) params.set('zone_id', filters.zone_id);
    if (filters?.event_type) params.set('event_type', filters.event_type);
    if (filters?.voucher_tier) params.set('voucher_tier', filters.voucher_tier);
    if (filters?.day) params.set('day', filters.day);
    const qs = params.toString();
    const url = `/events/weekly${qs ? `?${qs}` : ''}`;
    const { data } = signal
      ? await httpClient.get<EventItem[]>(url, { signal })
      : await httpClient.get<EventItem[]>(url);
    return data;
  },

  async getDetail(eventId: string): Promise<EventItem> {
    const { data } = await httpClient.get<EventItem>(`/events/${eventId}`);
    return data;
  },

  async getAttendees(eventId: string) {
    const { data } = await httpClient.get(`/events/${eventId}/attendees`);
    return data;
  },

  async joinWaitlist(eventId: string) {
    const { data } = await httpClient.post(`/events/${eventId}/waitlist`);
    return data;
  },

  async leaveWaitlist(eventId: string) {
    const { data } = await httpClient.del(`/events/${eventId}/waitlist`);
    return data;
  },

  async rateEvent(eventId: string, rating: { overall_rating: number; venue_rating?: number; would_attend_again?: string; comment?: string }) {
    const { data } = await httpClient.post(`/events/${eventId}/rate`, rating);
    return data;
  },

  async shareEvent(eventId: string) {
    const { data } = await httpClient.post(`/events/${eventId}/share`);
    return data;
  },
};

export default eventsService;
